import { useState, useEffect } from "react";
import { PublicFooter } from "@/components/Footer";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo } from "@/components/Logo";
import { Handshake, CheckCircle2, TrendingUp, Users, Wallet, Loader2, ArrowRight } from "lucide-react";
import RevenueCalculator from "@/components/RevenueCalculator";

const WAITLIST = "__waitlist__";

const PERKS = [
  { icon: TrendingUp, title: "20% commission", body: "Earn 20% on every subscriber you refer — for 6 months on monthly plans." },
  { icon: Users, title: "Two-tier earnings", body: "Get paid when your referrals bring their own friends, too." },
  { icon: Wallet, title: "Real cash payouts", body: "Paid via Stripe once you reach a $50 balance. No fees to join." },
];

export default function Partners() {
  const [form, setForm] = useState({ name: "", email: "", payout_country: "US", audience: "", links: "", waitlist_country: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("apply");
  const [countries, setCountries] = useState([]);
  const [waitlistDone, setWaitlistDone] = useState(false);

  useEffect(() => {
    api.get("/affiliates/supported-countries").then(({ data }) => setCountries(data || [])).catch(() => {});
  }, []);

  const isWaitlist = form.payout_country === WAITLIST;
  const isPaystack = countries.find((c) => c.code === form.payout_country)?.method === "paystack";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const goToCalculator = () => {
    setTab("calculator");
    setTimeout(() => document.getElementById("partners-calc-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (isWaitlist) {
      if (!form.email.trim() || !form.waitlist_country.trim()) {
        setError("Please enter your email and your country.");
        return;
      }
      setSubmitting(true);
      try {
        await api.post("/affiliates/waitlist", { name: form.name.trim(), email: form.email.trim(), country: form.waitlist_country.trim() });
        setWaitlistDone(true);
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || "Something went wrong. Please try again.");
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (!form.name.trim() || !form.email.trim() || !form.audience.trim()) {
      setError("Please fill in your name, email and audience description.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/affiliates/apply", {
        name: form.name.trim(),
        email: form.email.trim(),
        payout_country: form.payout_country,
        audience: form.audience.trim(),
        links: form.links.split("\n").map((l) => l.trim()).filter(Boolean),
      });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white">
      <header className="border-b border-zinc-200 bg-white/70 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo imgClassName="h-8" />
          <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">Sign in</Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary bg-gold/15 px-3 py-1 rounded-full">
            <Handshake className="h-3.5 w-3.5 text-gold" /> Jobsmith Affiliate Program
          </span>
          <h1 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight mt-4">Earn cash for every job seeker you send our way</h1>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">For creators, career coaches and print shops. Share Jobsmith with your audience and earn 20% commission on every paid subscription.</p>
          <button type="button" onClick={goToCalculator} data-testid="hero-calculator-cta"
            className="inline-flex items-center gap-1.5 mt-5 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-[#1F3864] shadow-sm hover:brightness-95 active:scale-[0.98] transition-all">
            Estimate my earnings <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-12">
          {PERKS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="rounded-xl border border-zinc-200 bg-white p-5">
                <div className="h-9 w-9 rounded-lg bg-[#24468C]/10 flex items-center justify-center mb-3">
                  <Icon className="h-5 w-5 text-[#24468C]" strokeWidth={1.75} />
                </div>
                <p className="font-heading font-semibold">{p.title}</p>
                <p className="text-sm text-muted-foreground mt-1">{p.body}</p>
              </div>
            );
          })}
        </div>

        <div id="partners-calc-anchor" className="flex justify-center gap-2 mb-8 scroll-mt-20" data-testid="partners-tabs">
          <button type="button" onClick={() => setTab("apply")} data-testid="partners-tab-apply"
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "apply" ? "bg-[#24468C] text-white" : "bg-white border border-zinc-200 text-muted-foreground hover:text-foreground"}`}>Become an affiliate</button>
          <button type="button" onClick={() => setTab("calculator")} data-testid="partners-tab-calculator"
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "calculator" ? "bg-[#24468C] text-white" : "bg-white border border-zinc-200 text-muted-foreground hover:text-foreground"}`}>Revenue calculator</button>
        </div>

        {tab === "calculator" ? (
          <RevenueCalculator />
        ) : (
        <div className="max-w-lg mx-auto">
          {waitlistDone ? (
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-8 text-center" data-testid="affiliate-waitlist-success">
              <CheckCircle2 className="h-12 w-12 mx-auto text-blue-600" strokeWidth={1.5} />
              <h2 className="font-heading text-2xl font-bold mt-4 text-blue-900">You're on the waitlist!</h2>
              <p className="text-sm text-blue-800 mt-2">Thanks — we'll email <strong>{form.email}</strong> as soon as affiliate payouts are available in <strong>{form.waitlist_country}</strong>.</p>
            </div>
          ) : done ? (
            <div className="rounded-xl border border-green-200 bg-green-50 p-8 text-center" data-testid="affiliate-apply-success">
              <CheckCircle2 className="h-12 w-12 mx-auto text-green-600" strokeWidth={1.5} />
              <h2 className="font-heading text-2xl font-bold mt-4 text-green-900">Application received!</h2>
              <p className="text-sm text-green-800 mt-2">Our team reviews every application manually. We'll email you at <strong>{form.email}</strong> once a decision is made.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-xl shadow-sm p-6 sm:p-8 space-y-5" data-testid="affiliate-apply-form">
              <h2 className="font-heading text-2xl font-bold">Apply to become an affiliate</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider">Full name *</Label>
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="aff-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider">Email *</Label>
                  <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="aff-email" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Payout country *</Label>
                <Select value={form.payout_country} onValueChange={(v) => set("payout_country", v)}>
                  <SelectTrigger data-testid="aff-country"><SelectValue placeholder="Select your country" /></SelectTrigger>
                  <SelectContent>
                    {countries.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                    <SelectItem value={WAITLIST} data-testid="aff-country-waitlist">🌍 My country isn't listed…</SelectItem>
                  </SelectContent>
                </Select>
                {isWaitlist ? (
                  <p className="text-xs text-muted-foreground">We can only pay affiliates in countries our payout provider (Stripe) supports today. Join the waitlist and we'll notify you the moment your country is added.</p>
                ) : isPaystack ? (
                  <p className="text-xs text-emerald-700" data-testid="aff-paystack-note">Payouts in <strong>Nigeria</strong> are made via <strong>Paystack</strong> (direct bank transfer, in NGN). You can apply and start earning now — we'll email you the moment Paystack payout setup opens.</p>
                ) : (
                  <p className="text-xs text-muted-foreground">These are the countries we can pay out to via Stripe today. Used to route the right tax form during onboarding.</p>
                )}
              </div>
              {isWaitlist && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider">Your country *</Label>
                  <Input value={form.waitlist_country} onChange={(e) => set("waitlist_country", e.target.value)} placeholder="e.g. Nigeria" data-testid="aff-waitlist-country" />
                </div>
              )}
              {!isWaitlist && (<>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Your audience / channel *</Label>
                <Textarea rows={3} value={form.audience} onChange={(e) => set("audience", e.target.value)} placeholder="e.g. Instagram — career & CV content, 40k followers / Retail resume-printing shop" data-testid="aff-audience" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Links to your profiles / evidence of reach</Label>
                <Textarea rows={3} value={form.links} onChange={(e) => set("links", e.target.value)} placeholder="One link per line (Instagram, TikTok, YouTube, website…)" data-testid="aff-links" />
              </div>
              </>)}
              {error && <p className="text-sm text-destructive" data-testid="aff-apply-error">{error}</p>}
              <Button type="submit" disabled={submitting} className="w-full h-11 bg-[#24468C] hover:bg-[#1d3870] active:scale-[0.98] transition-transform" data-testid="aff-apply-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (isWaitlist ? "Join the waitlist" : "Submit application")}
              </Button>
              <p className="text-xs text-center text-muted-foreground">No fee, ever. Applications are reviewed manually — most jurisdictions require you to disclose paid promotion (e.g. "#ad").</p>
            </form>
          )}
        </div>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}
